import { useEffect, useState } from "react";
import { isCapacitor } from "../lib/capacitor";
import { openInstallatiePagina, zoekNieuweVersie, type NieuweVersie } from "../lib/update";

/**
 * Groene balk bovenaan zodra er een nieuwere APK klaarstaat.
 *
 * De Windows-app werkt zichzelf bij; Android heeft niets. Een nieuwe versie
 * stond dus op de server zonder dat iemand het merkte. Deze balk is dat sein.
 *
 * Boven alles, in de kleur die niets stukmaakt: het is geen storing, er is
 * alleen iets nieuws. Aantikken opent de installatiepagina in de browser - niet
 * in de app zelf, want een app-venster kan geen bestand downloaden en Android
 * laat alleen een echte browser een installatie starten.
 *
 * Het kruisje verbergt hem tot de volgende keer dat je de app opent. Wegklikken
 * mag; vergeten ook, want dan staat hij er morgen weer.
 */
const HERHAAL_MS = 60 * 60 * 1000;

export default function UpdateBanner() {
  const [nieuw, setNieuw] = useState<NieuweVersie | null>(null);
  const [weg, setWeg] = useState(false);
  const [bezig, setBezig] = useState(false);

  useEffect(() => {
    if (!isCapacitor()) return;
    let levend = true;
    const kijk = () => {
      void zoekNieuweVersie().then((v) => {
        if (levend) setNieuw(v);
      });
    };

    kijk();
    /* Bij terugkomen uit de achtergrond opnieuw kijken - dat is het moment
       waarop iemand de app oppakt - en verder eens per uur, voor wie hem
       dagenlang open laat staan. */
    const opnieuw = () => {
      if (document.visibilityState === "visible") kijk();
    };
    document.addEventListener("visibilitychange", opnieuw);
    const timer = setInterval(kijk, HERHAAL_MS);
    return () => {
      levend = false;
      document.removeEventListener("visibilitychange", opnieuw);
      clearInterval(timer);
    };
  }, []);

  if (!nieuw || weg) return null;

  async function bijwerken() {
    setBezig(true);
    try {
      await openInstallatiePagina();
    } finally {
      setBezig(false);
    }
  }

  return (
    /* Donkere tekst op dit groen, geen witte. Wit haalt op #4aa622 net geen
       4,5:1 en dat is precies de soort balk die je in de zon moet kunnen lezen;
       zwart komt op 5,5:1 uit. */
    <div className="flex shrink-0 items-center gap-2 bg-brand-green px-3 py-2 text-sm text-slate-950">
      <button
        onClick={() => void bijwerken()}
        disabled={bezig}
        className="min-w-0 flex-1 text-left font-medium disabled:opacity-70"
      >
        Er is een nieuwe versie ({nieuw.versie}). Tik hier om bij te werken.
      </button>
      <button
        onClick={() => setWeg(true)}
        aria-label="Verbergen"
        className="shrink-0 rounded px-2 py-1 text-slate-950/60 hover:bg-black/10 hover:text-slate-950"
      >
        ✕
      </button>
    </div>
  );
}
